<template>
  <b-modal
    :active="true"
    has-modal-card
    full-screen
    :can-cancel="false"
    custom-class="standup__modal-background"
  >
    <div v-if="isLoading" style="position: relative; min-height: 150px">
      <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading"></b-loading>
    </div>
    <div v-else class="modal-card">
      <nav
        class="navbar is-fixed-top team-standup__navigation"
        role="navigation"
        aria-label="standup navigation"
      >
        <div class="navbar-menu">
          <div class="navbar-start">
            <div class="navbar-item">
              <user-avatar
                :user="users[activeTeam.teamLeaderId]"
                size="small"
                custom-class="u-m-r-small"
              />
              <b-tag type="is-primary-light" rounded>Team Leader</b-tag>
            </div>
          </div>
          <div class="navbar-end">
            <div class="navbar-item">
              <b-button
                type="is-text"
                @click="handleClickLeaveStandup"
                @keyup.enter="handleClickLeaveStandup"
              >
                Leave
              </b-button>
            </div>
          </div>
        </div>
      </nav>
      <section class="section modal-card-body">
        <h5 class="title is-5">
          {{ activeTeam.teamName }}
          <span class="is-size-6 has-text-weight-normal">({{ activeTeamUsers.length }} Members)</span>
        </h5>
        <p class="subtitle">
          {{ new Date().toLocaleString(undefined, defaultDateOptions) }}
        </p>
        <div class="card">
          <header
            v-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-header"
          >
            <span class="card-header-title">
              <user-avatar :user="users[activeStandup.currentReportingUserId]" size="small" />
              &nbsp;&nbsp;
              <user-display-name :active-user-id="activeStandup.currentReportingUserId" size="small" />
            </span>
          </header>
          <div
            v-if="!activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-content has-text-centered"
          >
            <h5 class="title is-5">Welcome to standup!</h5>
            <b-button
              type="is-info"
              @click.prevent="handleClickBeginReporting"
              @keyup.enter="handleClickBeginReporting"
            >
              Begin Reporting
            </b-button>
          </div>
          <div
            v-else-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-content"
          >
            <daily-goal
              v-for="teamReportingDate in teamReportingDatesInISO"
              :key="teamReportingDate.key"
              :activeUserId="activeStandup.currentReportingUserId"
              :goalDate="teamReportingDate"
            />
          </div>
          <div v-else-if="activeStandup.isStandupReportingComplete" class="card-content has-text-centered">
            <h5 class="title is-5">Standup reporting completed!</h5>
            <p class="u-m-b-large">Does anyone have any questions, comments, announcements or big wins?</p>
            <b-button
              type="is-info"
              @click.prevent="handleClickLeaveStandup"
              @keyup.enter="handleClickLeaveStandup"
            >
              Leave Standup
            </b-button>
          </div>
        </div>
      </section>
      <footer class="team-standup__footer">
        <!--<div class="team-standup__footer__section team-standup__footer__timer__wrapper">
          <div class="team-standup__footer__timer">
            {{ (standupTimerMinutes.toString())
                .padStart(2, '0') }}:{{ (standupTimerSeconds.toString()).padStart(2, '0')
            }}
          </div>
        </div>-->
        <div class="team-standup__footer__users-queue__wrapper">
          <span
            v-for="user in alreadyReportedUsers"
            :key="user.id" class="users-queue__avatar"
          >
            <b-icon
              v-if="activeStandup.currentReportingUserId === user.id"
              icon="menu-down"
              type="is-primary"
              custom-class="users-queue__reporting-icon"
            />
            <user-avatar
              :key="user.id"
              :user="user"
              size="small"
              is-clickable
              is-faded
              @click.native="handleSelectReportingUser(user.id)"
            />
          </span>
          <span v-if="alreadyReportedUsers.length" class="users-queue__divider" />
          <span
            v-for="user in usersPendingReport"
            :key="user.id" class="users-queue__avatar"
          >
            <b-icon
              v-if="activeStandup.currentReportingUserId === user.id"
              icon="menu-down"
              type="is-primary"
              custom-class="users-queue__reporting-icon"
            />
            <user-avatar
              :key="user.id"
              :user="user"
              size="small"
              is-clickable
              @click.native="handleSelectReportingUser(user.id)"
            />
          </span>
        </div>
        <div
          v-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
          class="team-standup__footer__end"
        >
          <b-button
            type="is-dark"
            outlined
            class="u-m-r"
            @click="handleClickEndReporting"
            @keyup.enter="handleClickEndReporting"
          >
            End Reporting
          </b-button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as UserActions from '@/store/actions/User.actions';
import * as StandupActions from '@/store/actions/Standup.actions';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as GoalMutations from '@/store/mutations/Goal.mutations';
import * as StandupMutations from '@/store/mutations/Standup.mutations';
import { isObjectEmpty, dictionaryFromDocumentArray, documentWithId } from '@/helpers/dataHelpers';
import { defaultDateOptions, dateToISO, getLastOccuringDayOfWeekFromDate } from '@/helpers/dateHelpers';
import { daysOfTheWeek } from '@/constants/TimeAndDateConstants';
import * as Types from '@/constants/Types';
import * as DatabaseService from '@/services/Database.service';
import * as collections from '@/constants/dbCollections';

import UserAvatar from '@/components/UserAvatar.vue';
import DailyGoal from '@/components/DailyGoal.vue';
import UserDisplayName from '@/components/UserDisplayName.vue';

export default {
  name: 'TeamStandupView',

  components: {
    UserAvatar,
    DailyGoal,
    UserDisplayName,
  },

  data() {
    return {
      standupTimerSeconds: 0,
      standupTimerMinutes: 0,
      isInitialDataFetching: true,
      unsubscribeStandupListener: null,
      unsubscribeGoalsListeners: null,
      defaultDateOptions,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
      users: state => state.user.users,
      standups: state => state.standup.standups,
      goals: state => state.goal.goals,
    }),
    ...mapGetters([
      'activeWorkspace',
    ]),
    activeStandup() {
      if (isObjectEmpty(this.standups) || !this.$route.params.standupId) return {};

      return this.standups[this.$route.params.standupId];
    },
    activeTeam() {
      if (
        isObjectEmpty(this.teams)
        || !this.$route.params.standupId
        || isObjectEmpty(this.activeStandup)
      ) return {};

      return this.teams[this.activeStandup.teamId];
    },
    activeTeamUsers() {
      if (isObjectEmpty(this.activeTeam)) return [];

      return Object.values(this.users).filter(user => this.activeTeam.userIds.includes(user.id));
    },
    alreadyReportedUsers() {
      return this.activeTeamUsers.filter(user => this.activeStandup.alreadyReportedUserIds.includes(user.id));
    },
    usersPendingReport() {
      return this.activeTeamUsers.filter(user => !this.activeStandup.alreadyReportedUserIds.includes(user.id));
    },
    isLoading() {
      return this.isInitialDataFetching;
    },
    teamGoalIds() {
      return Object.values(this.goals)
        .filter(goal => this.activeTeam.userIds.includes(goal.userId))
        .map(goal => goal.id);
    },
    // Function to find the current day, AND the most recent previous reporting day
    // based on the team's reporting settings.
    teamReportingDatesInISO() { // Returns dates as  'YYYY-MM-DD'
      if (isObjectEmpty(this.activeTeam)) return [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const teamReportingDaysForCurrentStandup = [];
      let sortedDays = null;

      if (this.activeTeam.standupReportingDayIds.includes(Types.TYPE_REPORTING_DAY_TODAY)) {
        teamReportingDaysForCurrentStandup.push(dateToISO(today));
      }

      // If team needs to report on 'yesterday's' goal then we need to find the actual previous day
      // based on the team settings. In many cases it is not truly yesterday, despite the product nomenclature.
      if (this.activeTeam.standupReportingDayIds.includes(Types.TYPE_REPORTING_DAY_YESTERDAY)) {
        const sortedTeamStandupDays = [...this.activeTeam.standupMeetingDayIds]
          .sort((a, b) => b - a)
          .map(dayId => daysOfTheWeek.find(day => day.id === dayId));

        const todayWeekDayIndex = today.getDay();
        const previousTeamStandupDay = this.findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex);
        const previousDayDate = getLastOccuringDayOfWeekFromDate(today, previousTeamStandupDay.weekDayIndex);

        // If last occuring date function returns same date (due to range being outside 6 days)
        // Make sure we are referring to the PREVIOUS week. E.G: Friday --> Last Friday.
        if (previousDayDate.getTime() === today.getTime()) {
          previousDayDate.setDate(previousDayDate.getDate() - 7);
        }

        sortedDays = [dateToISO(previousDayDate), ...teamReportingDaysForCurrentStandup];
      }

      return sortedDays || teamReportingDaysForCurrentStandup;
    },
  },

  async created() {
    await this.fetchInitialStandupData();

    this.subscribeToStandupChangeEvents();
    this.subscribeToGoalsChangeEvents();
  },

  beforeDestroy() {
    this.unsubscribeStandupListener();
    this.unsubscribeGoalsListeners.map(goalQueryListener => goalQueryListener());
  },

  methods: {
    ...mapActions({
      fetchTeams: TeamActions.FETCH_TEAMS_BY_IDS,
      fetchWorkspaceUsersByIds: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchGoalsByUserIds: GoalActions.FETCH_GOALS_BY_USER_IDS,
      fetchStandupsByIds: StandupActions.FETCH_STANDUPS_BY_IDS,
      updateStandup: StandupActions.UPDATE_STANDUP,
    }),
    ...mapMutations({
      receiveGoals: GoalMutations.RECEIVE_GOALS,
      receiveStandups: StandupMutations.RECEIVE_STANDUPS,
    }),
    handleStandupUpdate(standupSnapshot) {
      const shapedDocument = documentWithId(standupSnapshot);
      this.receiveStandups({ standups: { [shapedDocument.id]: shapedDocument } });
    },
    handleGoalUpdate(goalsQuerySnapshot) {
      if (goalsQuerySnapshot.docChanges().length > 0) {
        const updatedGoalDocuments = goalsQuerySnapshot.docChanges().map(change => change.doc);
        const shapedGoalDocuments = dictionaryFromDocumentArray(updatedGoalDocuments);

        this.receiveGoals(shapedGoalDocuments);
      }
    },
    handleClickLeaveStandup() {
      this.$router.push('/dashboard');
    },
    handleClickEndReporting() {
      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: { hasReportingStarted: false, isStandupReportingComplete: true },
      });
    },
    handleClickBeginReporting() {
      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: { hasReportingStarted: true },
      });
    },
    // Recursive function to iterate backward through team standup days and find the closest
    // weekday that matches.
    findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex) {
      if (!sortedTeamStandupDays.find(day => day.weekDayIndex === todayWeekDayIndex - 1) && todayWeekDayIndex > 0) {
        return this.findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex - 1);
      }
      // If it's a sunday (0), reset weekday value back to saturday (6) instead of going negative.
      if (todayWeekDayIndex === 0) {
        return this.findPreviousTeamReportingDay(sortedTeamStandupDays, 6);
      }

      return sortedTeamStandupDays.find(day => day.weekDayIndex === todayWeekDayIndex - 1);
    },
    /* handleClickBeginReporting() {
      this.startTimer();
    },
    startTimer() {
      this.standupTimerInterval = setInterval(() => {
        if (this.standupTimerSeconds < 59) {
          this.standupTimerSeconds = this.standupTimerSeconds + 1;
        } else if (this.standupTimerSeconds === 59) {
          this.standupTimerMinutes = this.standupTimerMinutes + 1;
          this.standupTimerSeconds = 0;
        }
      }, 1000);
    }, */
    handleSelectReportingUser(userId) {
      const alreadyReportedUserIds = [...this.activeStandup.alreadyReportedUserIds];
      const updatedUserIds = alreadyReportedUserIds
        .filter(alreadyReportedUserId => userId !== alreadyReportedUserId);

      updatedUserIds.push(this.activeStandup.currentReportingUserId);

      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: {
          currentReportingUserId: userId,
          alreadyReportedUserIds: [...updatedUserIds],
        },
      });
    },
    async subscribeToStandupChangeEvents() {
      this.unsubscribeStandupListener = await DatabaseService
        .getDocumentReference(collections.STANDUPS, this.activeStandup.id)
        .onSnapshot(this.handleStandupUpdate);
    },
    async subscribeToGoalsChangeEvents() {
      this.unsubscribeGoalsListeners = await Promise.all(
        this.activeTeam.userIds
          .map(userId => DatabaseService.getGoalsRefsByUserId(userId, this.activeWorkspace.id).onSnapshot(this.handleGoalUpdate)),
      );
    },
    async fetchInitialStandupData() {
      return new Promise(async (resolve) => {
        await this.fetchStandupsByIds({ standupIds: [this.$route.params.standupId] });
        await this.fetchTeams({ teamIds: [this.activeStandup.teamId] });
        await this.fetchWorkspaceUsersByIds({
          userIds: [...this.activeTeam.userIds],
          workspaceId: this.activeWorkspace.id,
        });

        if (!this.activeStandup.currentReportingUserId) {
          await this.updateStandup({
            standupId: this.activeStandup.id,
            updatedStandup: { currentReportingUserId: this.activeTeamUsers[0].id },
          });
        }

        await this.fetchGoalsByUserIds({ userIds: [...this.activeTeam.userIds], workspaceId: this.activeWorkspace.id });

        this.isInitialDataFetching = false;
        resolve();
      });
    },
  },
};
</script>

<style lang="scss">
.team-standup__body {
  background-color: $skwad-light-grey;
}

.standup__modal-background {
  & .modal {
    background-color: transparent!important;
  }
}

.team-standup__navigation {
  box-shadow: $light-shadow;
}

.modal-card-body {
  background-color: $body-background-color;
  margin-top: $navbar-height;
}

.team-standup__footer {
  position: fixed;
  display: flex;
  justify-content: space-between;
  bottom: 0;
  width: 100%;
  background-color: $white;
  min-height: $navbar-height + $global-whitespace-base;
}

.team-standup__footer__section {
  height: $navbar-height + $global-whitespace-base;
  padding: 0 $global-whitespace-base;
  display: flex;

  & > div {
    align-self: center;
  }
}

.team-standup__footer__timer__wrapper {
  box-shadow: 6px 0 10px -2px $grey-lighter;
  min-width: 100px;
}

.team-standup__footer__timer {
  font-size: $size-5;
  font-weight: $weight-bold;
  color: $primary;
}

.team-standup__footer__users-queue__wrapper {
  display: flex;
  align-items: center;
  padding: 0 $global-whitespace-base;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
}

.team-standup__footer__end {
  justify-content: flex-end;
  align-self: center;
}

.users-queue__avatar {
  position: relative;
  margin-right: $global-whitespace-small;
  cursor: pointer;

   > span { // Selects the icon wrapper.
     height: 0;
     position: absolute;
     top: -9px;
     left: 7px;
  }
}

.users-queue__reporting-icon {
  position: absolute;
}

.users-queue__divider {
  padding: 0 $global-whitespace-xsmall;
  border-left: 1px solid $grey-light;
  height: 100%;

  &:after {
    content: ' ';
  }
}
</style>
